import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useSearchParams, useParams, useNavigate } from 'react-router';
import { CrmLibraryItemForm } from './components/CrmLibraryItemForm';
import { CrmLibraryItemPreview } from './components/CrmLibraryItemPreview';
import { CrmItemViewModel } from '../../types/domain';
import { SelectOption } from '../../components/common/Dropdown/types';
import { createLibraryItem, updateLibraryItem } from '../../api/libraryItem/api';
import { showErrorToast } from '@/hooks/use-toast';
import { LoadingOverlay } from '@/components/common/Loading';
import { Button } from '@/components/shadcn/button';
import { faCheck, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const CrmLibraryItemCreateEditPage = () => {
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const pipelineId = searchParams.get('pipelineid');
  const groupId = searchParams.get('groupid');
  const libraryItemId = id;
  const isEditMode = !!libraryItemId;
  const [hasValidationError, setHasValidationError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState<{ 
    groupOptions: SelectOption[]; 
    visibleColumnsOptions: SelectOption[]; 
    sortColumnOptions: SelectOption[]; 
    displayByOptions: SelectOption[];
    libraryItemTypeOptions: SelectOption[];
    displayStyleOptions: SelectOption[];
    dateFilterModeOptions: SelectOption[];
    availableFilters: any[];
    pipelineOptions: SelectOption[];
    multiSelectSelectedOptions: Record<string, SelectOption[]>;
    availablePipelineStages: SelectOption[];
  }>({ 
    groupOptions: [], 
    visibleColumnsOptions: [], 
    sortColumnOptions: [], 
    displayByOptions: [],
    libraryItemTypeOptions: [],
    displayStyleOptions: [],
    dateFilterModeOptions: [],
    availableFilters: [],
    pipelineOptions: [],
    multiSelectSelectedOptions: {},
    availablePipelineStages: []
  });

  const formMethods = useForm<CrmItemViewModel>();

  const onSubmit = async (data: CrmItemViewModel) => {
    setSubmitting(true);
    try {
      if (isEditMode) {
        await updateLibraryItem(libraryItemId, data);
      } else {
        await createLibraryItem(data);
      }

      // Redirect to home page after successful operation
      window.location.href = '/MyHome.aspx';
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} library item:`, error);
      showErrorToast(`Failed to ${isEditMode ? 'update' : 'create'} library item. Please try again.`);
      setSubmitting(false);
    }
  };

  const handleSubmit = formMethods.handleSubmit(onSubmit, () => {
    setHasValidationError(true);
  });

  const handleCancel = () => {
    navigate(-1); // Go back to previous page, could be improved to go to a specific page if needed
  };

  return (
    <div className="px-4 py-4">
      <LoadingOverlay 
        isVisible={submitting} 
        message={ isEditMode ? "Updating library item..." : "Creating library item..."} 
      />
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Form Section */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="mb-6 flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">Configuration</h2>
                  <div className="flex justify-end space-x-3 mt-2">
                    <Button type="button" variant="outline" onClick={handleCancel} className='text-kPurple-500'>
                      Cancel
                    </Button>
                    <Button type="submit" className='bg-kPurple-500 hover:bg-kPurple-700 text-white hover:text-white font-bold'>
                      {libraryItemId ? 'Update' : 'Create'}
                      <FontAwesomeIcon icon={libraryItemId ? faCheck : faPlus} />
                    </Button>
                  </div>
                </div>
                <CrmLibraryItemForm 
                  formMethods={formMethods} 
                  hasValidationError={hasValidationError} 
                  onValidationErrorHandled={() => setHasValidationError(false)} 
                  pipelineId={pipelineId}
                  libraryItemId={libraryItemId}
                  groupId={groupId}
                  onOptionsChange={setDropdownOptions}
                  onCancel={handleCancel}
                />
              </div>
            </div>

            {/* Preview Section */}
            <div className="space-y-6">
              <div className="sticky top-40">
                  <CrmLibraryItemPreview 
                    dropdownOptions={dropdownOptions} 
                    availableFilters={dropdownOptions.availableFilters}
                    multiSelectSelectedOptions={dropdownOptions.multiSelectSelectedOptions}
                  />
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
